import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import * as firebaseCourses from '../../services/firebaseCourses';
import { getProgress, setLessonComplete } from '../../services/firebaseProgress';
import { useAuth } from '../../context/AuthContext';
import { getMergedEnrollments } from '../../utils/enrollmentStore';
import { hasFullCourseAccess, isTextLesson, isVideoLesson } from '../../utils/lessonUtils';
import LessonQuiz from '../../components/LessonQuiz/LessonQuiz';
import './TextLesson.css';

function flattenLessons(curriculum) {
  return (curriculum || []).flatMap((s) => (s?.lessons || []).map((l) => ({ ...l, _sectionId: s.id })));
}

export default function TextLesson() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolledInCourse, setEnrolledInCourse] = useState(false);
  const [completedLessonIds, setCompletedLessonIds] = useState([]);
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    firebaseCourses
      .getCourse(courseId)
      .then((detail) => {
        if (isMounted) setCourse(detail || null);
      })
      .catch(() => {
        if (isMounted) setCourse(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [courseId]);

  useEffect(() => {
    if (!user?.uid || !courseId) {
      setEnrolledInCourse(false);
      return;
    }
    getMergedEnrollments(user.uid)
      .then((list) => {
        setEnrolledInCourse((list || []).some((c) => String(c.id) === String(courseId)));
      })
      .catch(() => setEnrolledInCourse(false));
  }, [user?.uid, courseId]);

  useEffect(() => {
    if (!user?.uid || !courseId) return;
    getProgress(user.uid, courseId).then(({ completedLessonIds: ids, progressPercent: pct }) => {
      setCompletedLessonIds(ids);
      setProgressPercent(pct || 0);
    });
  }, [user?.uid, courseId]);

  const allLessons = useMemo(() => flattenLessons(course?.curriculum), [course?.curriculum]);
  const totalLessons = allLessons.length;
  const hasFullAccess = course ? hasFullCourseAccess(course.price, enrolledInCourse) : false;

  // Optimistically keep the progress bar in sync with local completed lessons,
  // so it increases immediately on lesson-to-lesson navigation.
  useEffect(() => {
    if (totalLessons < 1) return;
    const optimistic = Math.min(100, Math.round(((completedLessonIds?.length || 0) / totalLessons) * 100));
    setProgressPercent((prev) => (optimistic > prev ? optimistic : prev));
  }, [completedLessonIds, totalLessons]);

  const firstVideoLesson = useMemo(
    () => allLessons.find((l) => isVideoLesson(l)) ?? null,
    [allLessons]
  );

  /** Paid & not enrolled → checkout; otherwise first real video lesson (text lesson ids redirect back from /learn). */
  const switchToVideoHref = useMemo(() => {
    if (!courseId) return null;
    if (!hasFullAccess) {
      return `/course/${courseId}/pay`;
    }
    if (firstVideoLesson) {
      return `/course/${courseId}/learn/${firstVideoLesson.id}`;
    }
    return null;
  }, [courseId, hasFullAccess, firstVideoLesson]);

  const currentLesson = useMemo(() => {
    if (!course?.curriculum?.length || lessonId == null || lessonId === '') return null;
    return allLessons.find((l) => String(l.id) === String(lessonId)) ?? null;
  }, [allLessons, course?.curriculum?.length, lessonId]);

  const currentIndex = useMemo(() => {
    if (!currentLesson) return -1;
    return allLessons.findIndex((l) => String(l.id) === String(currentLesson.id));
  }, [allLessons, currentLesson]);

  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 && currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  // Redirect: if someone lands on /read for a video lesson, send them to /learn
  useEffect(() => {
    if (!courseId || !currentLesson) return;
    if (!isTextLesson(currentLesson)) {
      navigate(`/course/${courseId}/learn/${currentLesson.id}`, { replace: true });
    }
  }, [courseId, currentLesson, navigate]);

  // Mark lesson complete (text lessons are always accessible, even in paid courses)
  useEffect(() => {
    if (!user?.uid || !courseId || !currentLesson || totalLessons < 1) return;
    const idStr = String(currentLesson.id);
    if (completedLessonIds.includes(idStr)) return;

    setCompletedLessonIds((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
    setLessonComplete(user.uid, courseId, currentLesson.id, totalLessons)
      .then((newPercent) => {
        setProgressPercent(newPercent);
        getProgress(user.uid, courseId).then(({ completedLessonIds: ids }) => setCompletedLessonIds(ids));
      })
      .catch(() => {
        setCompletedLessonIds((prev) => prev.filter((id) => id !== idStr));
        getProgress(user.uid, courseId).then(({ completedLessonIds: ids, progressPercent: pct }) => {
          setCompletedLessonIds(ids);
          setProgressPercent(pct || 0);
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, courseId, currentLesson?.id, totalLessons]);

  if (loading || !course) {
    return (
      <div className="text-lesson-layout">
        <div className="text-lesson-main">
          <p className="text-lesson-loading">Loading lesson…</p>
        </div>
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <div className="text-lesson-layout">
        <div className="text-lesson-main">
          <div className="text-lesson-inner">
            <p className="text-lesson-loading">This lesson could not be found.</p>
            <p className="text-lesson-empty">
              <Link to={`/course/${courseId}`}>Return to course</Link>
              {' · '}
              <Link to={`/course/${courseId}/overview`}>Course overview</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const html = currentLesson?.content || '';
  const quiz = currentLesson?.quiz || null;

  return (
    <div className="text-lesson-layout">
      <div className="text-lesson-shell">
        <Sidebar
          curriculum={course.curriculum}
          courseId={courseId}
          currentLessonId={currentLesson?.id}
          completedLessonIds={completedLessonIds}
          progressPercent={progressPercent}
          coursePrice={course.price}
          hasFullAccess={hasFullAccess}
        />

        <main className="text-lesson-main">
          <div className="text-lesson-inner">
            <header className="text-lesson-header">
              <div className="text-lesson-breadcrumb">
                <Link to={`/course/${courseId}/overview`} className="text-lesson-breadcrumb-link">
                  {course.title || 'Course'}
                </Link>
                <span className="text-lesson-breadcrumb-sep">/</span>
                <span className="text-lesson-breadcrumb-current">Text lesson</span>
              </div>
              <h1 className="text-lesson-title">{currentLesson?.title || 'Lesson'}</h1>
              <div className="text-lesson-actions">
                <Link to={`/course/${courseId}/overview`} className="text-lesson-action-link">
                  Back to course
                </Link>
                {switchToVideoHref ? (
                  <Link
                    to={switchToVideoHref}
                    className="text-lesson-action-link"
                    title={hasFullAccess ? undefined : 'Complete payment to unlock video lessons'}
                  >
                    Switch to video view
                  </Link>
                ) : null}
              </div>
            </header>

            <article className="text-lesson-article">
              {html ? (
                <div className="text-lesson-html" dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <p className="text-lesson-empty">No lesson content yet. Check back soon.</p>
              )}
            </article>

            <LessonQuiz
              quiz={quiz}
              userId={user?.uid}
              courseId={courseId}
              lessonId={String(currentLesson?.id || '')}
            />

            <nav className="text-lesson-pager" aria-label="Lesson navigation">
              <div className="text-lesson-pager-col">
                {prevLesson ? (
                  <Link
                    to={`/course/${courseId}/${isTextLesson(prevLesson) ? 'read' : 'learn'}/${prevLesson.id}`}
                    className="text-lesson-pager-btn"
                  >
                    <span className="text-lesson-pager-kicker">Previous</span>
                    <span className="text-lesson-pager-title">{prevLesson.title}</span>
                  </Link>
                ) : (
                  <div className="text-lesson-pager-btn disabled" aria-disabled="true">
                    <span className="text-lesson-pager-kicker">Previous</span>
                    <span className="text-lesson-pager-title">—</span>
                  </div>
                )}
              </div>
              <div className="text-lesson-pager-col">
                {nextLesson ? (
                  <Link
                    to={`/course/${courseId}/${isTextLesson(nextLesson) ? 'read' : 'learn'}/${nextLesson.id}`}
                    className="text-lesson-pager-btn right"
                  >
                    <span className="text-lesson-pager-kicker">Next</span>
                    <span className="text-lesson-pager-title">{nextLesson.title}</span>
                  </Link>
                ) : (
                  <div className="text-lesson-pager-btn right disabled" aria-disabled="true">
                    <span className="text-lesson-pager-kicker">Next</span>
                    <span className="text-lesson-pager-title">—</span>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </main>
      </div>
    </div>
  );
}

